<template>
    <div ref="$mainMenuWrapper" class="main-menu-wrapper">
        <ul class="main-menu">
            <li v-for="menuItem in headerMenu" :key="menuItem.id" class="menu-item level-1" :class="{ 'w-children': !!menuItem.children?.length }">
                <button
                    v-if="menuItem?.type == 'verbb\\navigation\\nodetypes\\PassiveType'"
                    class="menu-link"
                    :class="{ active: currentMenuItem?.id == menuItem?.id }"
                    type="button"
                    @click="showSubMenu(menuItem)"
                >
                    {{ menuItem.title }}
                </button>

                <NuxtLinkLocale
                    v-else
                    :to="useConvertedMenuLink(menuItem)"
                    :target="menuItem.newWindow ? '_blank' : '_self'"
                    :external="menuItem.newWindow == '_blank'"
                    :title="menuItem.title"
                    >{{ menuItem.title }}</NuxtLinkLocale
                >
            </li>
        </ul>

        <div ref="$menuChildren" class="main-menu-children">
            <div ref="$menuChildrenInner" class="main-menu-children--inner">
                <div v-if="currentMenuItem?.children?.length" ref="$menuChildrenGrid" class="main-menu-children--grid">
                    <ul class="main-menu-childrens" :class="[isLocations(currentMenuItem?.children)]">
                        <li v-for="item in currentMenuItem?.children" :key="item.id" class="menu-item level-2">
                            <span v-if="item.type == 'verbb\\navigation\\nodetypes\\PassiveType'" class="passive label">{{ item.title }}</span>

                            <div v-if="item.type == 'craft\\elements\\Entry' && item.element.sectionHandle == 'locations'" class="menu-card-location">
                                <NuxtLinkLocale :to="useConvertedLink(item)">
                                    <div class="menu-card-location--thumbnail">
                                        <UiImage :src="item.element.locationPreview[0].webp" :classes="'responsive full'" />
                                    </div>
                                </NuxtLinkLocale>
                                <div class="menu-card-location--title">{{ item.title }}</div>
                            </div>

                            <NuxtLinkLocale
                                v-else
                                :to="useConvertedMenuLink(item)"
                                :target="item.newWindow ? '_blank' : '_self'"
                                :external="item.newWindow == '_blank'"
                                :title="item.title"
                                >{{ item.title }}</NuxtLinkLocale
                            >
                        </li>
                    </ul>

                    <div v-for="card in currentMenuItem.menuCard ?? []" :key="card.id" class="menu-card">
                        <div class="menu-card--thumbnail">
                            <div class="card-image">
                                <UiImage :src="card?.cardMedia[0].webp" :classes="'responsive full'" />
                            </div>
                            <div class="card-title h3">{{ card.cardLabel }}</div>
                            <div v-if="!!card?.cardLink.length" class="card-link">
                                <UiLink :link="card?.cardLink[0]" :style="'white'" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn, onClickOutside } from '@vueuse/core';
import type { CmsItem } from '~/types/cms';

const { $gsap: gsap } = useNuxtApp();
const isSubMenu = ref(false);
const $mainMenuWrapper = ref<HTMLDivElement | null>(null);
const $menuChildren = ref<HTMLDivElement | null>(null);
const $menuChildrenGrid = ref<HTMLDivElement | null>(null);

const menuData = useMenusData();
const scrollState = useScrollState();
const route = useRoute();
const headerMenu = computed<CmsItem[]>(() => menuData.value.menu_header.filter(item => item.siteId == useSiteId()));
const currentMenuItem = ref<CmsItem | null>(null);

const showSubMenu = useDebounceFn((item: CmsItem) => {
    if (item.children?.length) {
        if (currentMenuItem.value && currentMenuItem.value?.id == item.id) {
            isSubMenu.value = !isSubMenu.value;
        } else {
            isSubMenu.value = true;
        }
    } else {
        isSubMenu.value = false;
    }
    currentMenuItem.value = item;
}, 250);

watch(
    () => route,
    () => {
        isSubMenu.value = false;
    },
    {
        deep: true
    }
);

watchDebounced(
    () => scrollState.value,
    () => {
        isSubMenu.value = false;
    }
);

watch([() => isSubMenu.value, () => currentMenuItem.value], ([showMenu], [, itemMenuOld]) => {
    if (showMenu) {
        if (itemMenuOld == null) {
            gsap.to($menuChildren.value!, {
                height: 'auto',
                opacity: 1,
                duration: 0.25,
                ease: 'sine.in',
                pointerEvents: 'initial',
                onStart: () => {
                    document.body.classList.add('is-menu-open');
                    gsap.set($menuChildrenGrid.value!, {
                        opacity: 0
                    });
                },
                onComplete: () => {
                    gsap.to($menuChildrenGrid.value!, {
                        opacity: 1,
                        duration: 0.25,
                        ease: 'sine.in'
                    });
                }
            });
        } else {
            gsap.set($menuChildrenGrid.value!, {
                opacity: 0
            });

            gsap.to($menuChildrenGrid.value!, {
                opacity: 1,
                duration: 0.25,
                ease: 'sine.in'
            });
        }
    } else {
        gsap.to($menuChildren.value!, {
            height: 0,
            opacity: 0,
            pointerEvents: 'none',
            duration: 0.3,
            ease: 'sine.out',
            onComplete: () => {
                document.body.classList.remove('is-menu-open');
                $menuChildren.value!.style.pointerEvents = 'none';
                currentMenuItem.value = null;
            }
        });
    }
});

onClickOutside($mainMenuWrapper, () => (isSubMenu.value = false));

const isLocations = (children: CmsItem[] = []) => {
    if (!children.length) return '';
    const isExist = children.some(item => item.type == 'craft\\elements\\Entry' && item.element?.sectionHandle == 'locations');

    return isExist ? 'row' : 'column';
};
</script>
